import {
	applyPercentEdit,
	getDrainOrder,
	isAllocationTotalWithinLimit,
} from "@/shared/utils/allocationSplit";
import { useState } from "react";
import {
	findOverlappingPeriod,
	formatBillingDate,
	WaterBillGridRow,
	WaterBillPeriod,
} from "../../../utils/billRows";

const getSplitError = (
	row: WaterBillGridRow,
	targetCodes: string[],
): string | null => {
	if (targetCodes.length === 0) return null;

	const total = targetCodes.reduce(
		(sum, code) => sum + (row.percentByTarget[code] ?? 0),
		0,
	);
	return isAllocationTotalWithinLimit(total)
		? null
		: `% to Mill cannot go over 100 (now ${total.toLocaleString(undefined, { maximumFractionDigits: 2 })})`;
};

const getPeriodError = (
	periods: WaterBillPeriod[],
	row: WaterBillGridRow,
): string | null => {
	const clash = findOverlappingPeriod(
		periods,
		row.servicePeriodStart,
		row.servicePeriodEnd,
		row.billId,
	);
	return clash === null
		? null
		: `Overlaps the bill of ${formatBillingDate(clash.start)} → ${formatBillingDate(clash.end)}`;
};

type UseBillEditingArgs = {
	rows: WaterBillGridRow[];
	draftRows: WaterBillGridRow[];
	setDraftRows: React.Dispatch<React.SetStateAction<WaterBillGridRow[]>>;
	targetCodes: string[];
	periods: WaterBillPeriod[];
	editingBillId: number | null;
	setEditingBillId: (billId: number | null) => void;
	onBillSave: (rows: WaterBillGridRow[]) => void;
};

export type BillEditing = ReturnType<typeof useBillEditing>;

export const useBillEditing = ({
	rows,
	draftRows,
	setDraftRows,
	targetCodes,
	periods,
	editingBillId,
	setEditingBillId,
	onBillSave,
}: UseBillEditingArgs) => {
	const [percentOrder, setPercentOrder] = useState<string[]>([]);
	const lastPercentCode = percentOrder[percentOrder.length - 1] ?? null;

	const patchRow = (id: string, patch: Partial<WaterBillGridRow>) =>
		setDraftRows((current) =>
			current.map((row) => (row.id === id ? { ...row, ...patch } : row)),
		);

	const patchPercent = (
		row: WaterBillGridRow,
		code: string,
		value: number | null,
	) => {
		const nextOrder = [...percentOrder.filter((other) => other !== code), code];
		setPercentOrder(nextOrder);
		patchRow(row.id, {
			percentByTarget: applyPercentEdit(
				row.percentByTarget,
				targetCodes,
				code,
				value,
				nextOrder,
			),
		});
	};

	const openSession = (billId: number | null) => {
		setPercentOrder([]);
		setEditingBillId(billId);
	};

	return {
		patchRow,
		patchPercent,

		isEditing: (billId: number) => editingBillId === billId,

		isLocked: (billId: number) =>
			editingBillId !== null && editingBillId !== billId,

		isPeriodDirty: (billId: number) => {
			if (editingBillId !== billId) return false;

			const findHeader = (source: WaterBillGridRow[]) =>
				source.find((row) => row.billId === billId && row.isFirstOfBill);
			const saved = findHeader(rows);
			const draft = findHeader(draftRows);
			if (saved === undefined || draft === undefined) return false;

			return (
				saved.servicePeriodStart?.getTime() !==
					draft.servicePeriodStart?.getTime() ||
				saved.servicePeriodEnd?.getTime() !== draft.servicePeriodEnd?.getTime()
			);
		},

		balancingCode:
			lastPercentCode === null
				? null
				: (getDrainOrder(targetCodes, lastPercentCode, percentOrder)[0] ??
					null),

		getError: (row: WaterBillGridRow) =>
			editingBillId !== row.billId || !row.isFirstOfBill
				? null
				: (getPeriodError(periods, row) ?? getSplitError(row, targetCodes)),

		startEdit: (billId: number) => openSession(billId),

		cancelEdit: () => {
			setDraftRows(rows);
			openSession(null);
		},

		confirmEdit: (billId: number) => {
			openSession(null);
			onBillSave(draftRows.filter((row) => row.billId === billId));
		},
	};
};
